import { t as uiText, jaText, currentUiLanguage } from '../i18n';
import { useState, useCallback, useRef, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDocs,
  orderBy,
  query,
  limit,
  serverTimestamp,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { functions, db } from '../lib/firebase';
import { generateEmbeddingsForBoards } from '../utils/generateEmbeddingsForBoards';
import type { AgentChatMessage, AgentChatAction, FinalizeAgentChatLogRequest } from '../../functions/src/agentAdvisor/agentChatFn';
import { guidePresentation } from '../utils/agentGuideSource';
import {
  applyIngestTransition,
  coerceChoicesForRestore,
  coerceFlowForRestore,
  inferIngestFlowFromMessages,
  IDLE_INGEST_FLOW,
  normalizeIngestFlow,
  extractUrlsFromText,
  normalizeUrl,
  AFTER_SUCCESS_CHOICES,
  AFTER_SCOPE_CRAWL_CHOICES,
  AGENT_REGISTER_URL_LIMIT,
  SEED_ONLY_CHOICE,
  DISCOVER_CHILDREN_CHOICE,
  MORE_URLS_CHOICE,
  CRAWL_NOW_CHOICE,
  CONTINUE_SAME_WORK,
  START_NEW_WORK,
  continueSameWorkGuidance,
  recoverPreviewUrlsFromMessages,
  markCrawlTargetsRegistered,
  urlsForAgentChatLlm,
  keepLongerUrlList,
  resolveConfirmedCrawlAction,
  choicesAfterSyncSuccess,
  choicesAfterAsyncJob,
  choicesForCrawlPreview,
  canonicalIngestChoiceSet,
  rewriteCrawlPreviewReply,
  isCrawlRegisterConfirm,
  type IngestFlowState,
  type IngestTransitionEvent,
  type ParsedPagesCsv,
} from '../utils/agentIngest';
import { countChildPages } from '../utils/agentPageCount';

export type { AgentChatMessage };

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  executedAction?: {
    label: string;
    success: boolean;
    resultText?: string;
    running?: boolean;
    trackingId?: string;
    trackingType?: 'crawlerExecution' | 'suggestRagRun';
  };
  choices?: string[];
  choicesUsed?: boolean;
  crawlTargetList?: { url: string; status: string }[];
  pagePreviewList?: string[];
  createdPage?: { boardId: string; title: string; slug: string };
  truncatedByLimit?: boolean;
  /** agentChatLogs id for this model turn. Not stored on the session. */
  logId?: string;
  /** Action the home actually ran. Not stored on the session. */
  actionType?: string;
}

interface AgentChatRequest {
  workspaceId: string;
  messages: AgentChatMessage[];
  language: string;
  extractedUrls?: string[];
  ingestFlow?: IngestFlowState;
  openPanel?: string | null;
  deferAnalytics?: boolean;
}

interface AgentChatResponse {
  reply: string;
  action?: AgentChatAction;
  choices?: string[];
  continuation?: { operationId: string; label: string };
  truncatedByLimit?: boolean;
  logId?: string;
}

// ---------------------------------------------------------------------------
// Action → callable mapping
// ---------------------------------------------------------------------------

const ACTION_TO_OPERATION: Partial<Record<string, string>> = {
  startCrawl: 'ingestFromWebsite.startCrawl',
  registerCrawlTarget: 'ingestFromWebsite.registerCrawlTargets',
  publishWorkspace: 'publishPublicRag.publish',
  runDRS: 'generateSuggestedQuestions.startRun',
};

const TRACKING_CONFIG: Partial<Record<string, {
  field: string;
  type: NonNullable<ChatMessage['executedAction']>['trackingType'];
}>> = {
  startCrawl: { field: 'executionId', type: 'crawlerExecution' },
  runDRS:     { field: 'runId',       type: 'suggestRagRun' },
};

interface ExecuteResult {
  success: boolean;
  resultText: string;
  trackingId?: string;
  trackingType?: NonNullable<ChatMessage['executedAction']>['trackingType'];
  crawlTargetList?: { url: string; status: string }[];
  pagePreviewList?: string[];
  createdPage?: { boardId: string; title: string; slug: string };
  skipped?: boolean;
}

interface ActionHelpers {
  onFrontendAction?: (action: AgentChatAction) => void;
  getPendingPagesCsv?: () => ParsedPagesCsv | null;
  clearPendingPagesCsv?: () => void;
  lastPreviewUrlsRef: { current: string[] };
}

function historyContent(m: ChatMessage): string {
  if (m.role !== 'assistant' || !m.executedAction?.resultText) return m.content;
  return uiText("{{content}}\n[Result] {{label}}: {{resultText}}", { content: m.content, label: m.executedAction.label, resultText: m.executedAction.resultText });
}

async function executeAction(
  action: AgentChatAction,
  workspaceId: string,
  helpers: ActionHelpers,
): Promise<ExecuteResult> {
  if (action.type === 'getCrawlTargets') {
    try {
      const snap = await getDocs(
        query(
          collection(db, `workspaces/${workspaceId}/urlCrawlTargets`),
          orderBy('createdAt', 'desc'),
          limit(50)
        )
      );
      const crawlTargetList = snap.docs.map((d) => {
        const data = d.data() as any;
        return { url: data.url ?? d.id, status: data.status ?? 'unknown' };
      });
      return { success: true, resultText: '', crawlTargetList };
    } catch (err: any) {
      return { success: false, resultText: err?.message ?? uiText("Failed to load the site list") };
    }
  }

  if (action.type === 'offerCsvTemplate') {
    helpers.onFrontendAction?.(action);
    const kind = action.params?.kind === 'pages' ? 'pages' : 'crawl';
    return {
      success: true,
      resultText: kind === 'pages'
        ? uiText("You can download a template. Drop a CSV on 📎 or into the chat. The first line can be a header or data.")
        : uiText("You can download a template. Drop a CSV on 📎 or into the chat. The first line can be a URL."),
    };
  }

  if (
    action.type === 'openAppearancePanel' ||
    action.type === 'openContactPanel' ||
    action.type === 'openEmbedSecurityPanel'
  ) {
    helpers.onFrontendAction?.(action);
    return {
      success: true,
      resultText: action.type === 'openAppearancePanel'
        ? uiText("Choose the appearance in the panel on the right")
        : action.type === 'openContactPanel'
        ? uiText("Choose the contact path in the panel on the right")
        : uiText("Register the host site in the panel on the right"),
    };
  }

  if (action.type === 'previewCrawlUrls') {
    const raw: unknown[] = Array.isArray(action.params?.urls) ? action.params.urls : [];
    const urls = Array.from(
      new Set(
        raw
          .filter((u): u is string => typeof u === 'string')
          .map((u) => normalizeUrl(u))
          .filter((u): u is string => !!u)
      )
    );
    helpers.lastPreviewUrlsRef.current = keepLongerUrlList(
      helpers.lastPreviewUrlsRef.current,
      urls
    );
    const displayUrls = helpers.lastPreviewUrlsRef.current;
    if (displayUrls.length === 0) {
      return { success: true, resultText: uiText("No valid URL was found"), crawlTargetList: [] };
    }

    // ── 既登録チェック（200件チャンク） ──────────────────────────────────────
    const workspaceId = action.params?.workspaceId as string | undefined;
    const existsByUrl: Record<string, boolean> = {};
    if (workspaceId) {
      try {
        const checkFn = httpsCallable(functions, 'checkUrlCrawlTargetsFn');
        const CHUNK = 200;
        for (let i = 0; i < displayUrls.length; i += CHUNK) {
          const chunk = displayUrls.slice(i, i + CHUNK);
          const res: any = await checkFn({ workspaceId, urls: chunk });
          const map: Record<string, { exists: boolean }> = res?.data?.existsByUrl ?? {};
          for (const [u, v] of Object.entries(map)) {
            existsByUrl[u] = v.exists;
          }
        }
      } catch (e) {
        console.warn('[previewCrawlUrls] existsCheck failed', e);
      }
    }

    const newUrls = displayUrls.filter((u) => !existsByUrl[u]);
    const existingCount = displayUrls.length - newUrls.length;

    helpers.lastPreviewUrlsRef.current = displayUrls;

    const existsNote = existingCount > 0 ? uiText("({{existingCount}} items skipped; already registered)", { existingCount: existingCount }) : '';
    const resultText = newUrls.length > 0
      ? uiText("Found {{length}} unregistered URLs{{existsNote}}", { length: newUrls.length, existsNote: existsNote })
      : uiText("All items are already registered{{existsNote}}", { existsNote: existsNote });

    return {
      success: true,
      resultText,
      crawlTargetList: displayUrls.map((url) => ({
        url,
        status: existsByUrl[url] ? 'exists' : 'new',
      })),
    };
  }

  if (action.type === 'importPagesCsv') {
    const pending = helpers.getPendingPagesCsv?.() ?? null;
    if (!pending || pending.rowCount === 0) {
      return { success: false, resultText: uiText("No CSV to import. Upload it again.") };
    }
    try {
      const processCsv = httpsCallable(functions, 'processCsvToNdjsonFn', { timeout: 120_000 });
      const res1: any = await processCsv({
        workspaceId,
        csvText: pending.csvText,
        hasHeader: pending.hasHeader,
        delimiter: pending.delimiter || ',',
        titleIndex: pending.titleIndex,
        contentIndex: pending.contentIndex,
      });
      const ndjsonGcsPath = res1?.data?.ndjsonGcsPath;
      const totalRecords = res1?.data?.totalRecords ?? 0;
      if (!ndjsonGcsPath) {
        return { success: false, resultText: uiText("Failed to convert the CSV") };
      }
      const importFn = httpsCallable(functions, 'importNdjsonToFirestoreFn', { timeout: 540_000 });
      const res2: any = await importFn({
        workspaceId,
        ndjsonGcsPath,
        folderId: null,
        duplicateHandling: 'skip',
        publishNewPagesAsAnswers: true,
      });
      const created: Array<{ boardId: string }> = res2?.data?.created || [];
      const updated: Array<{ boardId: string }> = res2?.data?.updated || [];
      const summary = {
        createdCount: res2?.data?.createdCount || 0,
        updatedCount: res2?.data?.updatedCount || 0,
        skippedCount: res2?.data?.skippedCount || 0,
        failedCount: res2?.data?.failedCount || 0,
      };
      const targets = [...created, ...updated];
      // AI 検索用データの作成まで待つ（知識軸 ON のページは公開 AI 用にも反映される）
      let embeddingFailed = 0;
      if (targets.length > 0) {
        const { failedBoardIds } = await generateEmbeddingsForBoards(
          workspaceId,
          targets.map((t) => t.boardId),
        );
        embeddingFailed = failedBoardIds.length;
      }
      helpers.clearPendingPagesCsv?.();
      return {
        success: summary.failedCount === 0 || summary.createdCount + summary.updatedCount > 0,
        resultText: uiText("New {{createdCount}} / Skipped {{skippedCount}} / Failed {{failedCount}} (converted {{totalRecords}} items)", { createdCount: summary.createdCount, skippedCount: summary.skippedCount, failedCount: summary.failedCount, totalRecords: totalRecords })
          + (embeddingFailed > 0
            ? "\n" + uiText("{{count}} items could not be prepared for AI search. The pages were created; open them and save again to retry.", { count: embeddingFailed })
            : ''),
      };
    } catch (err: any) {
      console.error('[useAgentChat] importPagesCsv error', err);
      return { success: false, resultText: err?.message ?? uiText("Failed to import page") };
    }
  }

  if (action.type === 'createPageFromText') {
    try {
      const fn = httpsCallable(functions, 'createPageFromTextFn', { timeout: 120_000 });
      const res: any = await fn({
        workspaceId,
        title: action.params?.title ?? '',
        content: action.params?.content ?? '',
      });
      const data = res.data as { boardId?: string; title?: string; slug?: string; skipped?: boolean };
      if (data?.boardId && !data.skipped) {
        const genEmbedding = httpsCallable(functions, 'generateEmbeddingFn');
        void genEmbedding({ workspaceId, boardId: data.boardId }).catch(() => undefined);
      }
      if (data?.skipped) {
        return {
          success: true,
          skipped: true,
          resultText: uiText("Skipped because a page with the same title already exists ({{title}})", { title: data.title }),
        };
      }
      const createdPage =
        data?.boardId && data?.title && data?.slug
          ? { boardId: data.boardId, title: data.title, slug: data.slug }
          : undefined;
      return {
        success: true,
        resultText: uiText("Created page \"{{v0}}\"", { v0: data?.title ?? '' }),
        createdPage,
      };
    } catch (err: any) {
      console.error('[useAgentChat] createPageFromText error', err);
      return { success: false, resultText: err?.message ?? uiText("Failed to create page") };
    }
  }

  const operationId = ACTION_TO_OPERATION[action.type];
  if (!operationId) {
    console.warn('[useAgentChat] No operation mapped for action:', action.type);
    return { success: false, resultText: uiText("This action is not supported yet") };
  }

  try {
    const data = action.alreadyExecuted
      ? (action.output ?? {})
      : await (async () => {
          const fn = httpsCallable(functions, 'executeAgentOperationFn', { timeout: 120_000 });
          const res = await fn({
            workspaceId,
            operationId,
            input: action.params ?? {},
          });
          const payload = res.data as { ok?: boolean; output?: any };
          return payload?.output ?? {};
        })();

    if (action.type === 'registerCrawlTarget') {
      const created: number = data?.createdCount ?? 0;
      const skipped: number = data?.skippedExistingCount ?? 0;
      const invalid: number = data?.invalidCount ?? 0;
      const skippedUrls: string[] = (data?.skippedExisting ?? []).map((s: any) => s.url as string);

      let resultText =
        created === 0 && skipped > 0
          ? uiText("{{skipped}} items are already registered", { skipped: skipped })
          : uiText("Registered {{created}} new items", { created: created });
      if (created > 0 && skipped > 0) {
        resultText += uiText("({{skipped}} items skipped; already registered: {{v1}}{{v2}})", { skipped: skipped, v1: skippedUrls.slice(0, 3).join(', '), v2: skippedUrls.length > 3 ? '...' : '' });
      }
      if (invalid > 0) {
        resultText += uiText(", {{invalid}} invalid URLs", { invalid: invalid });
      }
      return { success: true, resultText };
    }

    const trackingCfg = TRACKING_CONFIG[action.type];
    const trackingId: string | undefined = trackingCfg ? data?.[trackingCfg.field] : undefined;
    const trackingType = trackingCfg?.type;

    return { success: true, resultText: '', trackingId, trackingType };
  } catch (err: any) {
    console.error('[useAgentChat] executeAgentOperation error', err);
    const code = typeof err?.message === 'string' ? err.message : '';
    const friendly =
      code === 'insufficientPermission' ? uiText("You do not have permission to perform this action") :
      code === 'approvalRequired' ? uiText("This action requires approval") :
      code === 'activeJob' ? uiText("Cannot start while a crawl is running") :
      code === 'notAvailable' || code === 'notDispatchable' ? uiText("This action cannot be run from the agent right now") :
      err?.message ?? uiText("An error occurred while running");
    return { success: false, resultText: friendly };
  }
}

async function registerUrlsAndOfferCrawl(
  workspaceId: string,
  urls: string[],
  msgsAfterUser: ChatMessage[],
  helpers: ActionHelpers,
  intro: string,
): Promise<{ nextMsgs: ChatMessage[]; ok: boolean }> {
  const action: AgentChatAction = {
    type: 'registerCrawlTarget',
    label: uiText("Register URL"),
    params: { workspaceId, urls },
  };
  const result = await executeAction(action, workspaceId, helpers);
  const ok = result.success;
  const assistantMsg: ChatMessage = {
    id: uid(),
    role: 'assistant',
    content: ok
      ? uiText("{{intro}}{{resultText}}. Start the crawl? Tell me if you have more URLs to register.", { intro: intro, resultText: result.resultText })
      : uiText("Failed to register the URL. Please try again."),
    actionType: action.type,
    executedAction: {
      label: action.label,
      success: result.success,
      resultText: result.resultText,
    },
    ...(ok ? { choices: AFTER_SCOPE_CRAWL_CHOICES } : {}),
  };
  const prior = ok ? markCrawlTargetsRegistered(msgsAfterUser, urls) : msgsAfterUser;
  return { nextMsgs: [...prior, assistantMsg], ok };
}

function markActionStopped(
  msgs: ChatMessage[],
  messageId: string,
  resultText: string,
  success = true,
): ChatMessage[] {
  return msgs.map((m) => {
    if (m.id !== messageId || !m.executedAction) return m;
    return {
      ...m,
      executedAction: {
        ...m.executedAction,
        running: false,
        success,
        resultText,
      },
    };
  });
}

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

const lsKey = (workspaceId: string) => `agent-chat-v1-${workspaceId}`;

interface LsPayload {
  messages: ChatMessage[];
  ingestFlow: IngestFlowState;
}

function saveToLS(workspaceId: string, msgs: ChatMessage[], ingestFlow: IngestFlowState) {
  try {
    const payload: LsPayload = { messages: msgs, ingestFlow };
    localStorage.setItem(lsKey(workspaceId), JSON.stringify(payload));
  } catch (_) {}
}

function loadFromLS(workspaceId: string): LsPayload | null {
  try {
    const raw = localStorage.getItem(lsKey(workspaceId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // The parsed CSV only ever lived in memory, so nothing read back from storage
    // may offer a chip that needs it.
    if (Array.isArray(parsed)) {
      const messages = coerceChoicesForRestore(parsed as ChatMessage[], false);
      return { messages, ingestFlow: inferIngestFlowFromMessages(messages) };
    }
    if (parsed && typeof parsed === 'object' && Array.isArray(parsed.messages)) {
      const messages = coerceChoicesForRestore(parsed.messages as ChatMessage[], false);
      const flow = normalizeIngestFlow(parsed.ingestFlow);
      return {
        messages,
        ingestFlow: flow
          ? coerceFlowForRestore(flow, false)
          : inferIngestFlowFromMessages(messages),
      };
    }
    return null;
  } catch (_) {
    return null;
  }
}

function clearLS(workspaceId: string) {
  try { localStorage.removeItem(lsKey(workspaceId)); } catch (_) {}
}

// ---------------------------------------------------------------------------
// Firestore session helpers
// ---------------------------------------------------------------------------

/** Serialize messages for Firestore (strip tracking fields that are runtime-only) */
function serializeMsgs(msgs: ChatMessage[]) {
  return msgs.map((m) => ({
    id: m.id,
    role: m.role,
    content: m.content,
    ...(m.executedAction
      ? {
          executedAction: {
            label: m.executedAction.label,
            success: m.executedAction.success,
            resultText: m.executedAction.resultText ?? '',
          },
        }
      : {}),
    ...(m.choices ? { choices: m.choices } : {}),
    ...(m.crawlTargetList ? { crawlTargetList: m.crawlTargetList } : {}),
    ...(m.pagePreviewList ? { pagePreviewList: m.pagePreviewList } : {}),
    ...(m.createdPage ? { createdPage: m.createdPage } : {}),
    ...(m.truncatedByLimit ? { truncatedByLimit: true } : {}),
  }));
}

// ---------------------------------------------------------------------------
// Misc
// ---------------------------------------------------------------------------

function uid() { return Math.random().toString(36).slice(2); }

// ---------------------------------------------------------------------------
// Hook interface
// ---------------------------------------------------------------------------

export interface SendOptions {
  extractedUrls?: string[];
  pagePreviewList?: string[];
  skipUrlExtract?: boolean;
  localReply?: { content: string; choices?: string[] };
  /** Skip the agent callable and only persist the local user/assistant exchange. */
  skipAgent?: boolean;
}

interface UseAgentChatOptions {
  workspaceId: string;
  initialMessage?: string;
  onFrontendAction?: (action: AgentChatAction) => void;
  getPendingPagesCsv?: () => ParsedPagesCsv | null;
  clearPendingPagesCsv?: () => void;
  /** Called once when a conversation is first written to history (first user send). */
  onSessionPersisted?: (sessionId: string) => void;
  getNextWorkRecommendations?: () => { content: string; choices: string[] };
  /** Day 0 panel currently open on the home. Lets the server route a bare URL. */
  getOpenPanel?: () => string | null;
  /** Called when a background job finishes, so the caller can refetch its state. */
  onBackgroundJobFinished?: () => void;
}

export interface UseAgentChatResult {
  messages: ChatMessage[];
  sending: boolean;
  send: (text: string, options?: SendOptions) => Promise<void>;
  clearMessages: () => void;
  /** Restore messages from history. Pass sessionId so edits update the right doc. */
  restoreMessages: (
    msgs: ChatMessage[],
    sessionId?: string,
    ingestFlow?: IngestFlowState | null
  ) => void;
  prefillRef: React.MutableRefObject<((text: string) => void) | null>;
  markChoicesUsed: (messageId: string) => void;
  /** Current Firestore session id, if the conversation is already persisted */
  getSessionId: () => string | null;
  ingestFlow: IngestFlowState;
  applyIngestEvent: (event: IngestTransitionEvent) => void;
  createPageFromText: (input: { title: string; content: string }) => Promise<boolean>;
}

// ---------------------------------------------------------------------------
// Main hook
// ---------------------------------------------------------------------------

const SYNC_COMPLETING_ACTIONS = new Set([
  'importPagesCsv',
  'registerCrawlTarget',
  'createPageFromText',
  'publishWorkspace',
  'updateOGA',
]);

export function useAgentChat({
  workspaceId,
  initialMessage,
  onFrontendAction,
  getPendingPagesCsv,
  clearPendingPagesCsv,
  onSessionPersisted,
  getNextWorkRecommendations,
  getOpenPanel,
  onBackgroundJobFinished,
}: UseAgentChatOptions): UseAgentChatResult {
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    const saved = loadFromLS(workspaceId);
    if (saved && saved.messages.length > 0) return saved.messages;
    if (!initialMessage) return [];
    return [{ id: uid(), role: 'assistant', content: initialMessage }];
  });
  /** Assistant ids already sent to finalize, plus ones restored from an earlier session. */
  const guideLoggedIdsRef = useRef<Set<string>>(
    new Set(messages.filter((m) => m.role === 'assistant').map((m) => m.id))
  );
  const [ingestFlow, setIngestFlow] = useState<IngestFlowState>(() => {
    const saved = loadFromLS(workspaceId);
    return saved?.ingestFlow ?? { ...IDLE_INGEST_FLOW };
  });

  const [sending, setSending] = useState(false);
  const prefillRef = useRef<((text: string) => void) | null>(null);
  const ingestFlowRef = useRef<IngestFlowState>(ingestFlow);
  useEffect(() => { ingestFlowRef.current = ingestFlow; }, [ingestFlow]);

  /** Firestore session document ID for the current conversation */
  const sessionIdRef = useRef<string | null>(null);
  /** True until the first user+assistant exchange completes (for title generation) */
  const pendingTitleRef = useRef(true);
  /** Bumped on new chat so an in-flight persist does not attach to the new draft */
  const persistEpochRef = useRef(0);

  const unsubscribersRef = useRef<Map<string, Unsubscribe>>(new Map());
  const lastPreviewUrlsRef = useRef<string[]>(
    recoverPreviewUrlsFromMessages(loadFromLS(workspaceId)?.messages ?? [])
  );
  const onFrontendActionRef = useRef(onFrontendAction);
  const getPendingPagesCsvRef = useRef(getPendingPagesCsv);
  const clearPendingPagesCsvRef = useRef(clearPendingPagesCsv);
  const onSessionPersistedRef = useRef(onSessionPersisted);
  const getNextWorkRecommendationsRef = useRef(getNextWorkRecommendations);
  const getOpenPanelRef = useRef(getOpenPanel);
  const onBackgroundJobFinishedRef = useRef(onBackgroundJobFinished);
  useEffect(() => { getOpenPanelRef.current = getOpenPanel; }, [getOpenPanel]);
  useEffect(() => { onBackgroundJobFinishedRef.current = onBackgroundJobFinished; }, [onBackgroundJobFinished]);
  useEffect(() => { onFrontendActionRef.current = onFrontendAction; }, [onFrontendAction]);
  useEffect(() => { getPendingPagesCsvRef.current = getPendingPagesCsv; }, [getPendingPagesCsv]);
  useEffect(() => { clearPendingPagesCsvRef.current = clearPendingPagesCsv; }, [clearPendingPagesCsv]);
  useEffect(() => { onSessionPersistedRef.current = onSessionPersisted; }, [onSessionPersisted]);
  useEffect(() => { getNextWorkRecommendationsRef.current = getNextWorkRecommendations; }, [getNextWorkRecommendations]);

  // Track latest messages in a ref so async callbacks can read them
  const latestMessagesRef = useRef(messages);
  useEffect(() => { latestMessagesRef.current = messages; }, [messages]);

  // workspaceId changes → reload from localStorage
  const prevWorkspaceRef = useRef(workspaceId);
  useEffect(() => {
    if (prevWorkspaceRef.current === workspaceId) return;
    prevWorkspaceRef.current = workspaceId;
    sessionIdRef.current = null;
    pendingTitleRef.current = true;
    persistEpochRef.current += 1;
    const saved = loadFromLS(workspaceId);
    lastPreviewUrlsRef.current = recoverPreviewUrlsFromMessages(saved?.messages ?? []);
    const nextMessages = saved && saved.messages.length > 0
      ? saved.messages
      : initialMessage ? [{ id: uid(), role: 'assistant' as const, content: initialMessage }] : [];
    guideLoggedIdsRef.current = new Set(
      nextMessages.filter((m) => m.role === 'assistant').map((m) => m.id)
    );
    setMessages(nextMessages);
    const nextFlow = saved?.ingestFlow ?? { ...IDLE_INGEST_FLOW };
    ingestFlowRef.current = nextFlow;
    setIngestFlow(nextFlow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  // Seed initialMessage when LS is empty
  useEffect(() => {
    if (!initialMessage) return;
    setMessages((prev) => {
      if (prev.length > 0) return prev;
      const seeded = [{ id: uid(), role: 'assistant' as const, content: initialMessage }];
      saveToLS(workspaceId, seeded, ingestFlowRef.current);
      return seeded;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMessage]);

  // Persist to localStorage whenever messages or flow change
  useEffect(() => {
    if (messages.length > 0) saveToLS(workspaceId, messages, ingestFlow);
  }, [messages, ingestFlow, workspaceId]);

  // Clean up Firestore listeners on unmount
  useEffect(() => {
    const unsubs = unsubscribersRef.current;
    return () => { unsubs.forEach((u) => u()); unsubs.clear(); };
  }, []);

  // ---------------------------------------------------------------------------
  // Firestore session persistence
  // ---------------------------------------------------------------------------

  const persistSession = useCallback(
    async (msgs: ChatMessage[], flow?: IngestFlowState): Promise<void> => {
      if (!workspaceId || msgs.length === 0) return;
      if (!msgs.some((m) => m.role === 'user')) return;
      const serialized = serializeMsgs(msgs);
      const ingestFlowToSave = flow ?? ingestFlowRef.current;
      const existingId = sessionIdRef.current;
      const epoch = persistEpochRef.current;
      try {
        if (existingId) {
          await updateDoc(
            doc(db, `workspaces/${workspaceId}/agentChatSessions`, existingId),
            { messages: serialized, ingestFlow: ingestFlowToSave, updatedAt: serverTimestamp() }
          );
        } else {
          const firstUserMsg = msgs.find((m) => m.role === 'user');
          const tempTitle = firstUserMsg
            ? firstUserMsg.content.slice(0, 40) + (firstUserMsg.content.length > 40 ? '…' : '')
            : uiText("(New conversation)");
          const ref = await addDoc(
            collection(db, `workspaces/${workspaceId}/agentChatSessions`),
            {
              title: tempTitle,
              messages: serialized,
              ingestFlow: ingestFlowToSave,
              workspaceId,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            }
          );
          if (persistEpochRef.current === epoch && sessionIdRef.current === null) {
            sessionIdRef.current = ref.id;
            onSessionPersistedRef.current?.(ref.id);
          }
        }
        const sessionId = sessionIdRef.current;
        if (sessionId) {
          await recordPresentedGuide(msgs, ingestFlowToSave.step, sessionId);
        }
      } catch (err) {
        console.error('[useAgentChat] persistSession error', err);
      }
    },
    [workspaceId]
  );

  const recordPresentedGuide = useCallback(
    async (msgs: ChatMessage[], ingestStep: string, sessionId: string) => {
      const presentation = guidePresentation(msgs);
      if (!presentation || guideLoggedIdsRef.current.has(presentation.messageId)) return;
      guideLoggedIdsRef.current.add(presentation.messageId);
      try {
        const fn = httpsCallable<FinalizeAgentChatLogRequest, { ok: true }>(
          functions,
          'finalizeAgentChatLogFn'
        );
        await fn({
          workspaceId,
          ...(presentation.logId ? { logId: presentation.logId } : {}),
          sessionId,
          userMessage: presentation.userMessage,
          presentedReply: presentation.presentedReply,
          ...(presentation.choices ? { choices: presentation.choices } : {}),
          choiceSource: presentation.choiceSource,
          ingestStep,
          ...(presentation.actionType ? { actionType: presentation.actionType } : {}),
          ...(presentation.actionLabel ? { actionLabel: presentation.actionLabel } : {}),
          actionSuccess: presentation.actionSuccess,
          actionPending: presentation.actionPending,
          language: currentUiLanguage(),
          conversationLength: presentation.conversationLength,
        });
      } catch (err) {
        console.error('[useAgentChat] finalize guide log error', err);
      }
    },
    [workspaceId]
  );

  /** After the first exchange: ask AI to generate a better title */
  const generateAndSetTitle = useCallback(
    async (firstUserMessage: string, firstAssistantReply: string, targetId: string) => {
      if (!targetId) return;
      try {
        const fn = httpsCallable<
          { workspaceId: string; firstUserMessage: string; firstAssistantReply: string; language: string },
          { title: string }
        >(functions, 'generateAgentChatTitleFn');
        const res = await fn({
          workspaceId,
          firstUserMessage,
          firstAssistantReply,
          language: currentUiLanguage(),
        });
        const title = res.data.title;
        if (title) {
          await updateDoc(
            doc(db, `workspaces/${workspaceId}/agentChatSessions`, targetId),
            { title }
          );
        }
      } catch (err) {
        console.error('[useAgentChat] generateAndSetTitle error', err);
      }
    },
    [workspaceId]
  );

  // ---------------------------------------------------------------------------
  // Progress polling
  // ---------------------------------------------------------------------------

  const startProgressPolling = useCallback(
    (messageId: string, trackingId: string, trackingType: NonNullable<ChatMessage['executedAction']>['trackingType']) => {
      const collPath =
        trackingType === 'crawlerExecution'
          ? `workspaces/${workspaceId}/crawlerExecutions`
          : `workspaces/${workspaceId}/suggestRagRuns`;

      const unsub = onSnapshot(doc(db, collPath, trackingId), (snap) => {
        if (!snap.exists()) return;
        const data = snap.data() as any;
        const status: string = data.status ?? '';
        const isDone = ['completed', 'completed_with_issues', 'failed', 'partial'].includes(status);
        if (!isDone) return;

        const success = status !== 'failed';
        const resultText =
          status === 'partial' ? uiText("Errors occurred for some URLs") :
          status === 'completed_with_issues' ? uiText("There was a problem with some of the processing") :
          status === 'failed' ? uiText("An error occurred while processing") : '';

        setMessages((prev) => {
          const next = prev.map((m) =>
            m.id === messageId
              ? {
                  ...m,
                  // The body was written when the job started ("this may take a
                  // while"), so leaving it there reads as still running.
                  content: success && trackingType === 'crawlerExecution'
                    ? uiText("The crawl has finished. The knowledge is now searchable.")
                    : m.content,
                  executedAction: {
                    ...m.executedAction!,
                    label: success && trackingType === 'crawlerExecution'
                      ? uiText("Crawl finished")
                      : m.executedAction!.label,
                    running: false, success, resultText,
                    trackingId: undefined, trackingType: undefined,
                  },
                  ...(success
                    ? {
                        choices: choicesAfterAsyncJob({ success, trackingType }),
                        choicesUsed: false,
                      }
                    : {}),
                }
              : m
          );
          const flowAfter =
            success && trackingType === 'crawlerExecution'
              ? { ...IDLE_INGEST_FLOW }
              : ingestFlowRef.current;
          if (success && trackingType === 'crawlerExecution') {
            ingestFlowRef.current = flowAfter;
          }
          void persistSession(next, flowAfter);
          return next;
        });
        if (success && trackingType === 'crawlerExecution') {
          setIngestFlow({ ...IDLE_INGEST_FLOW });
        }

        // The status cards were fetched before the job ran, so they still show the
        // pre-crawl state until the caller refetches.
        onBackgroundJobFinishedRef.current?.();

        unsubscribersRef.current.get(messageId)?.();
        unsubscribersRef.current.delete(messageId);
      });

      unsubscribersRef.current.set(messageId, unsub);
    },
    [workspaceId, persistSession]
  );

  const commitIngestFlow = useCallback((next: IngestFlowState) => {
    ingestFlowRef.current = next;
    setIngestFlow(next);
  }, []);

  const applyIngestEvent = useCallback((event: IngestTransitionEvent) => {
    if (event.type === 'redo' || event.type === 'reset') {
      lastPreviewUrlsRef.current = [];
    }
    const next = applyIngestTransition(ingestFlowRef.current, event);
    commitIngestFlow(next);
    if (event.type === 'reset') {
      void persistSession(latestMessagesRef.current, next);
    }
  }, [commitIngestFlow, persistSession]);

  // ---------------------------------------------------------------------------
  // send
  // ---------------------------------------------------------------------------

  const send = useCallback(
    async (text: string, options?: SendOptions) => {
      if (!text.trim() || sending) return;
      setSending(true);

      // Retiring the old chips has to happen in the same array that gets the user
      // message: a separate setMessages is thrown away by the one below, because
      // latestMessagesRef only catches up on the next render.
      const retired = latestMessagesRef.current.map((m) =>
        m.choices && !m.choicesUsed ? { ...m, choicesUsed: true } : m
      );

      const userMsg: ChatMessage = { id: uid(), role: 'user', content: text.trim() };
      const msgsAfterUser = [...retired, userMsg];
      setMessages(msgsAfterUser);

      const trimmed = text.trim();
      if (options?.skipAgent) {
        const assistantMsg: ChatMessage = {
          id: uid(),
          role: 'assistant',
          content: options.localReply?.content ?? '',
          choices: options.localReply?.choices,
        };
        const nextMsgs = [...msgsAfterUser, assistantMsg];
        setMessages(nextMsgs);
        await persistSession(nextMsgs);
        setSending(false);
        return;
      }
      if (trimmed === CONTINUE_SAME_WORK || trimmed === START_NEW_WORK) {
        const before = ingestFlowRef.current;
        const nextFlow = applyIngestTransition(before, { type: 'choice', label: trimmed });
        const fallbackNew = getNextWorkRecommendationsRef.current?.() ?? {
          content: uiText("Switching tasks. Choose what you want to do next."),
          choices: [jaText("Crawl an existing site"), jaText("Add pages to notreu")],
        };
        const assistantMsg: ChatMessage = {
          id: uid(),
          role: 'assistant',
          content: options?.localReply?.content
            ?? (trimmed === CONTINUE_SAME_WORK
              ? continueSameWorkGuidance(before.path ? before : nextFlow)
              : fallbackNew.content),
          choices: options?.localReply?.choices
            ?? (trimmed === START_NEW_WORK || !before.path ? fallbackNew.choices : undefined),
        };
        const nextMsgs = [...msgsAfterUser, assistantMsg];
        setMessages(nextMsgs);
        commitIngestFlow(nextFlow);
        await persistSession(nextMsgs, nextFlow);
        setSending(false);
        return;
      }

      if (trimmed === SEED_ONLY_CHOICE) {
        if (lastPreviewUrlsRef.current.length === 0) {
          lastPreviewUrlsRef.current = recoverPreviewUrlsFromMessages(msgsAfterUser);
        }
        const urls = lastPreviewUrlsRef.current;
        const helpers: ActionHelpers = {
          onFrontendAction: onFrontendActionRef.current,
          getPendingPagesCsv: getPendingPagesCsvRef.current,
          clearPendingPagesCsv: clearPendingPagesCsvRef.current,
          lastPreviewUrlsRef,
        };
        if (urls.length === 0) {
          const assistantMsg: ChatMessage = {
            id: uid(),
            role: 'assistant',
            content: uiText("No target URL. Paste it again."),
          };
          const nextMsgs = [...msgsAfterUser, assistantMsg];
          setMessages(nextMsgs);
          await persistSession(nextMsgs);
          setSending(false);
          return;
        }
        const { nextMsgs, ok } = await registerUrlsAndOfferCrawl(
          workspaceId,
          urls,
          msgsAfterUser,
          helpers,
          uiText("Only this URL will be included."),
        );
        setMessages(nextMsgs);
        const flowAfter = ok
          ? applyIngestTransition(ingestFlowRef.current, { type: 'reset' })
          : ingestFlowRef.current;
        commitIngestFlow(flowAfter);
        await persistSession(nextMsgs, flowAfter);
        setSending(false);
        return;
      }

      if (trimmed === MORE_URLS_CHOICE) {
        const nextFlow = applyIngestTransition(ingestFlowRef.current, { type: 'choice', label: trimmed });
        const assistantMsg: ChatMessage = {
          id: uid(),
          role: 'assistant',
          content: uiText("Paste URLs to add. For CSV, drop a file or choose one with 📎."),
        };
        const nextMsgs = [...msgsAfterUser, assistantMsg];
        setMessages(nextMsgs);
        commitIngestFlow(nextFlow);
        await persistSession(nextMsgs, nextFlow);
        setSending(false);
        return;
      }

      if (trimmed === DISCOVER_CHILDREN_CHOICE) {
        if (lastPreviewUrlsRef.current.length === 0) {
          lastPreviewUrlsRef.current = recoverPreviewUrlsFromMessages(msgsAfterUser);
        }
        const baseUrl = lastPreviewUrlsRef.current[0];
        const helpers: ActionHelpers = {
          onFrontendAction: onFrontendActionRef.current,
          getPendingPagesCsv: getPendingPagesCsvRef.current,
          clearPendingPagesCsv: clearPendingPagesCsvRef.current,
          lastPreviewUrlsRef,
        };
        const countingMsg: ChatMessage = {
          id: uid(),
          role: 'assistant',
          content: baseUrl
            ? uiText("Checking how many pages are under {{baseUrl}}…", { baseUrl: baseUrl })
            : uiText("Cannot start a search under it because the target URL is unknown. Paste a URL."),
          executedAction: baseUrl
            ? { label: uiText("Child page count"), success: true, running: true }
            : undefined,
        };
        const countingMsgs = [...msgsAfterUser, countingMsg];
        setMessages(countingMsgs);
        await persistSession(countingMsgs);

        if (!baseUrl) {
          setSending(false);
          return;
        }

        try {
          const counted = await countChildPages(workspaceId, baseUrl);
          const n = counted.totalPages || counted.discoveredCount;
          const toRegister = Array.from(
            new Set([...lastPreviewUrlsRef.current, ...counted.discoveredUrls])
          ).slice(0, AGENT_REGISTER_URL_LIMIT);
          lastPreviewUrlsRef.current = toRegister;
          const capNote = n > AGENT_REGISTER_URL_LIMIT
            ? uiText("(Registering the first {{AGENT_REGISTER_URL_LIMIT}} items)", { AGENT_REGISTER_URL_LIMIT: AGENT_REGISTER_URL_LIMIT })
            : '';
          const countedMsgs = markActionStopped(countingMsgs, countingMsg.id, uiText("{{n}} pages", { n: n }));
          setMessages(countedMsgs);
          const { nextMsgs, ok } = await registerUrlsAndOfferCrawl(
            workspaceId,
            toRegister,
            countedMsgs,
            helpers,
            uiText("Found about {{n}} pages below{{capNote}}.", { n: n, capNote: capNote }),
          );
          setMessages(nextMsgs);
          const flowAfter = ok
            ? applyIngestTransition(ingestFlowRef.current, { type: 'reset' })
            : ingestFlowRef.current;
          commitIngestFlow(flowAfter);
          await persistSession(nextMsgs, flowAfter);
        } catch (err: any) {
          const failedNote = err?.message ?? uiText("Timed out");
          const countedMsgs = markActionStopped(countingMsgs, countingMsg.id, failedNote, false);
          setMessages(countedMsgs);
          const { nextMsgs, ok } = await registerUrlsAndOfferCrawl(
            workspaceId,
            lastPreviewUrlsRef.current,
            countedMsgs,
            helpers,
            uiText("Couldn't count child pages. You can still register this URL."),
          );
          const withError = nextMsgs.map((m, i) =>
            i === nextMsgs.length - 1
              ? {
                  ...m,
                  executedAction: {
                    ...(m.executedAction ?? { label: uiText("Register URL"), success: ok }),
                    resultText: `${m.executedAction?.resultText ?? ''}（${failedNote}）`,
                  },
                }
              : m
          );
          setMessages(withError);
          const flowAfter = ok
            ? applyIngestTransition(ingestFlowRef.current, { type: 'reset' })
            : ingestFlowRef.current;
          commitIngestFlow(flowAfter);
          await persistSession(withError, flowAfter);
        }
        setSending(false);
        return;
      }

      if (trimmed === CRAWL_NOW_CHOICE) {
        try {
          const helpers: ActionHelpers = {
            onFrontendAction: onFrontendActionRef.current,
            getPendingPagesCsv: getPendingPagesCsvRef.current,
            clearPendingPagesCsv: clearPendingPagesCsvRef.current,
            lastPreviewUrlsRef,
          };
          const action: AgentChatAction = {
            type: 'startCrawl',
            label: uiText("Start crawl"),
            params: { workspaceId },
          };
          const result = await executeAction(action, workspaceId, helpers);
          const isAsync = !!(result.trackingId && result.trackingType);
          const assistantMsg: ChatMessage = {
            id: uid(),
            role: 'assistant',
            content: result.success
              ? uiText("Crawl started. It may take a while to finish.")
              : uiText("Could not start the crawl."),
            executedAction: {
              label: action.label,
              success: result.success,
              resultText: isAsync ? '' : result.resultText,
              running: isAsync,
              trackingId: result.trackingId,
              trackingType: result.trackingType,
            },
          };
          const nextMsgs = [...msgsAfterUser, assistantMsg];
          setMessages(nextMsgs);
          await persistSession(nextMsgs);
          if (isAsync && result.trackingId && result.trackingType) {
            startProgressPolling(assistantMsg.id, result.trackingId, result.trackingType);
          }
        } finally {
          setSending(false);
        }
        return;
      }

      let nextFlow = applyIngestTransition(ingestFlowRef.current, {
        type: 'choice',
        label: trimmed,
      });

      const fromText = options?.skipUrlExtract ? [] : extractUrlsFromText(text);
      let extractedUrls = Array.from(
        new Set([
          ...(options?.extractedUrls ?? []),
          ...fromText,
        ])
      );
      if (nextFlow.path === 'pages') {
        extractedUrls = [];
      }
      const previewUrlsBeforeTurn = lastPreviewUrlsRef.current;
      if (extractedUrls.length > 0) {
        lastPreviewUrlsRef.current = extractedUrls;
      }

      const confirmPagesImport = text.trim() === jaText("Import this content");
      const confirmCrawlRegister = isCrawlRegisterConfirm(text);
      let crawlPreviewThisTurn =
        nextFlow.path !== 'pages' &&
        extractedUrls.length > 0 &&
        lastPreviewUrlsRef.current.length > 0;
      let crawlPreviewList = crawlPreviewThisTurn
        ? lastPreviewUrlsRef.current.map((url) => ({ url, status: 'preview' as const }))
        : undefined;

      try {
        const history: AgentChatMessage[] = msgsAfterUser
          .filter((m): m is ChatMessage & { role: 'user' | 'assistant' } =>
            m.role === 'user' || m.role === 'assistant'
          )
          .map((m) => ({ role: m.role, content: historyContent(m) }));

        const fn = httpsCallable<AgentChatRequest, AgentChatResponse>(functions, 'agentChatFn');
        const res = await fn({
          workspaceId,
          messages: history,
          language: currentUiLanguage(),
          extractedUrls: urlsForAgentChatLlm(extractedUrls),
          ingestFlow: nextFlow,
          openPanel: getOpenPanelRef.current?.() ?? null,
          deferAnalytics: true,
        });
        let { reply, action, choices, continuation, truncatedByLimit } = res.data;

        if (action?.type === 'openEmbedSecurityPanel') {
          // A host origin is not a crawl target: keep this turn out of the crawl preview
          // and leave the previous preview list untouched for a later ingest turn.
          crawlPreviewThisTurn = false;
          crawlPreviewList = undefined;
          lastPreviewUrlsRef.current = previewUrlsBeforeTurn;
          // The model keeps drifting into "crawl only this URL or pages under it?" here,
          // so the handoff wording is fixed rather than taken from the model.
          reply = uiText("Enter the host site URL in the panel on the right.");
        }

        if (action?.type === 'importPagesCsv' && !confirmPagesImport) {
          action = { type: 'none', label: '', params: {} };
        }
        if (action?.type === 'registerCrawlTarget' && !confirmCrawlRegister) {
          action = {
            type: 'previewCrawlUrls',
            label: uiText("Check URL"),
            params: { workspaceId, urls: lastPreviewUrlsRef.current },
          };
        }
        if (
          nextFlow.path !== 'pages' &&
          confirmCrawlRegister &&
          lastPreviewUrlsRef.current.length > 0
        ) {
          action = resolveConfirmedCrawlAction({
            action,
            lastPreviewUrls: lastPreviewUrlsRef.current,
            workspaceId,
          }) as AgentChatAction;
        }
        const pendingPagesRows = getPendingPagesCsvRef.current?.()?.rowCount ?? 0;
        if ((!action || action.type === 'none') && confirmPagesImport && pendingPagesRows > 0) {
          action = {
            type: 'importPagesCsv',
            label: uiText("Import page"),
            params: { workspaceId },
          };
        }
        if (confirmPagesImport && pendingPagesRows === 0) {
          // The parsed CSV is gone (session restore, reload), so nothing can be
          // imported. The model has already written "importing now" by this point,
          // which would leave the admin waiting on a turn that never runs.
          action = { type: 'none', label: '', params: {} };
          reply = uiText("No CSV to import. Upload it again.");
        }

        if (action && action.type !== 'none' && nextFlow.path === 'pages') {
          if (
            action.type === 'previewCrawlUrls' ||
            action.type === 'registerCrawlTarget' ||
            action.type === 'startCrawl' ||
            (action.type === 'createPageFromText' && nextFlow.input === 'text')
          ) {
            action = { type: 'none', label: '', params: {} };
          } else if (action.type === 'offerCsvTemplate') {
            action = { ...action, params: { ...action.params, kind: 'pages' } };
          }
        } else if (action?.type === 'offerCsvTemplate' && nextFlow.path === 'crawl') {
          action = { ...action, params: { ...action.params, kind: 'crawl' } };
        } else if (action?.type === 'createPageFromText' && !nextFlow.path) {
          const body = typeof action.params?.content === 'string' ? action.params.content.trim() : '';
          if (!body) {
            // The model asked for a page it has no body for, which is how it says
            // "let them type one". Open the card instead of creating an empty page.
            nextFlow = applyIngestTransition(nextFlow, {
              type: 'choice',
              label: jaText('Add pages to notreu'),
            });
            nextFlow = applyIngestTransition(nextFlow, {
              type: 'choice',
              label: jaText('Add one item as text'),
            });
            action = { type: 'none', label: '', params: {} };
          }
        }

        if (action?.type === 'offerCsvTemplate') {
          const kind = action.params?.kind === 'pages' ? 'pages' : 'crawl';
          nextFlow = applyIngestTransition(nextFlow, { type: 'offerCsvTemplate', kind });
        }
        if (
          options?.pagePreviewList &&
          options.pagePreviewList.length > 0
        ) {
          nextFlow = applyIngestTransition(nextFlow, { type: 'csvPreview' });
        }
        const applyCrawlPreview =
          crawlPreviewThisTurn ||
          (action?.type === 'previewCrawlUrls' && nextFlow.path === 'crawl');
        if (applyCrawlPreview) {
          nextFlow = applyIngestTransition(nextFlow, { type: 'csvPreview' });
        }

        if (applyCrawlPreview) {
          const n = lastPreviewUrlsRef.current.length || extractedUrls.length;
          choices = choicesForCrawlPreview(n);
          reply = rewriteCrawlPreviewReply(reply, n);
        } else {
          const wizardChoices = canonicalIngestChoiceSet(nextFlow, choices);
          if (wizardChoices) choices = wizardChoices;
        }
        if (options?.pagePreviewList && options.pagePreviewList.length > 0 && (!choices || choices.length === 0)) {
          choices = [jaText("Import this content"), jaText("Start over")];
        }

        const assistantMsg: ChatMessage = {
          id: uid(),
          role: 'assistant',
          content: reply,
          ...(res.data.logId ? { logId: res.data.logId } : {}),
          choices: choices && choices.length > 0 ? choices : undefined,
          pagePreviewList: options?.pagePreviewList,
          crawlTargetList: crawlPreviewList,
          truncatedByLimit: truncatedByLimit === true || undefined,
        };
        let workingMsgs = [...msgsAfterUser, assistantMsg];
        setMessages(workingMsgs);

        if (action && action.type !== 'none') {
          const helpers: ActionHelpers = {
            onFrontendAction: onFrontendActionRef.current,
            getPendingPagesCsv: getPendingPagesCsvRef.current,
            clearPendingPagesCsv: clearPendingPagesCsvRef.current,
            lastPreviewUrlsRef,
          };
          const { success, resultText, trackingId, trackingType, crawlTargetList, pagePreviewList } =
            await executeAction(action, workspaceId, helpers);

          const isAsync = !!(trackingId && trackingType);

          workingMsgs = workingMsgs.map((m) =>
            m.id === assistantMsg.id
              ? {
                  ...m,
                  actionType: action.type,
                  executedAction: {
                    label: action.label,
                    success: isAsync ? true : success,
                    resultText: isAsync ? '' : resultText,
                    running: isAsync,
                    trackingId: isAsync ? trackingId : undefined,
                    trackingType: isAsync ? trackingType : undefined,
                  },
                  crawlTargetList:
                    (crawlTargetList && crawlTargetList.length >= (m.crawlTargetList?.length ?? 0))
                      ? crawlTargetList
                      : (m.crawlTargetList ?? crawlTargetList),
                  pagePreviewList: pagePreviewList ?? m.pagePreviewList,
                }
              : m
          );
          setMessages(workingMsgs);

          if (isAsync) {
            startProgressPolling(assistantMsg.id, trackingId!, trackingType!);
          }

          if (action.type === 'previewCrawlUrls' || crawlPreviewThisTurn) {
            const n = lastPreviewUrlsRef.current.length;
            workingMsgs = workingMsgs.map((m) =>
              m.id === assistantMsg.id
                ? {
                    ...m,
                    content: rewriteCrawlPreviewReply(m.content, n),
                    choices: choicesForCrawlPreview(n),
                    choicesUsed: false,
                  }
                : m
            );
            setMessages(workingMsgs);
          }

          if (success && SYNC_COMPLETING_ACTIONS.has(action.type)) {
            nextFlow = applyIngestTransition(
              nextFlow,
              { type: action.type === 'registerCrawlTarget' ? 'reset' : 'confirmed' },
            );
            const nextChoices = choicesAfterSyncSuccess({
              continuation,
              existingChoices: choices,
              actionType: action.type,
            });
            if (action.type === 'registerCrawlTarget') {
              const registered = Array.isArray(action.params?.urls)
                ? (action.params.urls as string[])
                : lastPreviewUrlsRef.current;
              workingMsgs = markCrawlTargetsRegistered(workingMsgs, registered);
            }
            workingMsgs = workingMsgs.map((m) =>
              m.id === assistantMsg.id
                ? {
                    ...m,
                    choices: nextChoices,
                    choicesUsed: false,
                    ...(action.type === 'registerCrawlTarget'
                      ? {
                          content:
                            uiText("The URL has been registered. Start the crawl? Let me know if there are more URLs to register."),
                        }
                      : {}),
                  }
                : m
            );
            setMessages(workingMsgs);
          }
        }

        commitIngestFlow(nextFlow);
        await persistSession(workingMsgs, nextFlow);

        if (pendingTitleRef.current) {
          pendingTitleRef.current = false;
          const targetId = sessionIdRef.current;
          if (targetId) {
            void generateAndSetTitle(userMsg.content, reply, targetId);
          }
        }
      } catch (err: any) {
        console.error('[useAgentChat] send error', err);
        const previewUrls = lastPreviewUrlsRef.current;
        const canPreviewCrawl = nextFlow.path !== 'pages' && previewUrls.length > 0;
        const helpers: ActionHelpers = {
          onFrontendAction: onFrontendActionRef.current,
          getPendingPagesCsv: getPendingPagesCsvRef.current,
          clearPendingPagesCsv: clearPendingPagesCsvRef.current,
          lastPreviewUrlsRef,
        };

        if (confirmCrawlRegister && canPreviewCrawl) {
          const action: AgentChatAction = {
            type: 'registerCrawlTarget',
            label: uiText("Register URL"),
            params: { workspaceId, urls: previewUrls },
          };
          const result = await executeAction(action, workspaceId, helpers);
          const registeredOk = result.success;
          const assistantMsg: ChatMessage = {
            id: uid(),
            role: 'assistant',
            content: registeredOk
              ? uiText("{{v0}}. Start the crawl? Tell me if you have more URLs to register.", { v0: result.resultText || uiText("Registered {{length}} URLs", { length: previewUrls.length }) })
              : uiText("Failed to register the URL. Please try again."),
            actionType: action.type,
            executedAction: {
              label: action.label,
              success: registeredOk,
              resultText: result.resultText,
            },
            ...(registeredOk ? { choices: AFTER_SCOPE_CRAWL_CHOICES } : {}),
          };
          const prior = registeredOk
            ? markCrawlTargetsRegistered(msgsAfterUser, previewUrls)
            : msgsAfterUser;
          const nextMsgs = [...prior, assistantMsg];
          setMessages(nextMsgs);
          const flowAfter = registeredOk
            ? applyIngestTransition(nextFlow, { type: 'reset' })
            : nextFlow;
          commitIngestFlow(flowAfter);
          await persistSession(nextMsgs, flowAfter);
        } else if (canPreviewCrawl) {
          const previewFlow = applyIngestTransition(nextFlow, { type: 'csvPreview' });
          const assistantMsg: ChatMessage = {
            id: uid(),
            role: 'assistant',
            content: rewriteCrawlPreviewReply(
              uiText("Review {{length}} URLs.", { length: previewUrls.length }),
              previewUrls.length
            ),
            choices: choicesForCrawlPreview(previewUrls.length),
            crawlTargetList: previewUrls.map((url) => ({ url, status: 'preview' })),
          };
          const msgsWithPreview = [...msgsAfterUser, assistantMsg];
          setMessages(msgsWithPreview);
          commitIngestFlow(previewFlow);
          await persistSession(msgsWithPreview, previewFlow);
        } else {
          const errMsg: ChatMessage = {
            id: uid(), role: 'assistant',
            content: uiText("An error occurred. Please try again."),
          };
          const msgsWithErr = [...msgsAfterUser, errMsg];
          setMessages(msgsWithErr);
        }
      } finally {
        setSending(false);
      }
    },
    [workspaceId, sending, persistSession, generateAndSetTitle, startProgressPolling, commitIngestFlow]
  );

  // ---------------------------------------------------------------------------
  // clearMessages
  // ---------------------------------------------------------------------------

  const clearMessages = useCallback(() => {
    unsubscribersRef.current.forEach((u) => u());
    unsubscribersRef.current.clear();
    clearLS(workspaceId);
    sessionIdRef.current = null;
    pendingTitleRef.current = true;
    persistEpochRef.current += 1;
    lastPreviewUrlsRef.current = [];
    guideLoggedIdsRef.current = new Set();
    commitIngestFlow({ ...IDLE_INGEST_FLOW });
    setMessages([]);
  }, [workspaceId, commitIngestFlow]);

  // ---------------------------------------------------------------------------
  // restoreMessages
  // ---------------------------------------------------------------------------

  const restoreMessages = useCallback(
    (msgs: ChatMessage[], sessionId?: string, savedFlow?: IngestFlowState | null) => {
      unsubscribersRef.current.forEach((u) => u());
      unsubscribersRef.current.clear();
      sessionIdRef.current = sessionId ?? null;
      pendingTitleRef.current = !msgs.some((m) => m.role === 'user');
      lastPreviewUrlsRef.current = recoverPreviewUrlsFromMessages(msgs);
      const base = normalizeIngestFlow(savedFlow) ?? inferIngestFlowFromMessages(msgs);
      const hasPending = (getPendingPagesCsvRef.current?.()?.rowCount ?? 0) > 0;
      const next = coerceFlowForRestore(base, hasPending);
      const restored = coerceChoicesForRestore(msgs, hasPending);
      guideLoggedIdsRef.current = new Set(
        restored.filter((m) => m.role === 'assistant').map((m) => m.id)
      );
      commitIngestFlow(next);
      setMessages(restored);
      saveToLS(workspaceId, restored, next);
    },
    [workspaceId, commitIngestFlow]
  );

  // ---------------------------------------------------------------------------
  // markChoicesUsed
  // ---------------------------------------------------------------------------

  const markChoicesUsed = useCallback((messageId: string) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, choicesUsed: true } : m))
    );
  }, []);

  const getSessionId = useCallback(() => sessionIdRef.current, []);

  const createPageFromText = useCallback(
    async (input: { title: string; content: string }): Promise<boolean> => {
      const content = input.content.trim();
      if (!workspaceId || !content || sending) return false;
      setSending(true);
      try {
        const helpers: ActionHelpers = {
          onFrontendAction: onFrontendActionRef.current,
          getPendingPagesCsv: getPendingPagesCsvRef.current,
          clearPendingPagesCsv: clearPendingPagesCsvRef.current,
          lastPreviewUrlsRef,
        };
        const action: AgentChatAction = {
          type: 'createPageFromText',
          label: uiText("Create page"),
          params: { title: input.title.trim(), content },
        };
        const result = await executeAction(action, workspaceId, helpers);
        const createdOk = result.success && !result.skipped;
        const assistantMsg: ChatMessage = {
          id: uid(),
          role: 'assistant',
          content: result.success
            ? (result.skipped
              ? result.resultText
              : uiText("Created page \"{{v0}}\"", { v0: result.createdPage?.title ?? '' }))
            : uiText("Failed to create page"),
          actionType: action.type,
          executedAction: {
            label: action.label,
            success: result.success,
            resultText: result.resultText,
          },
          createdPage: result.skipped ? undefined : result.createdPage,
          ...(createdOk ? { choices: AFTER_SUCCESS_CHOICES } : {}),
        };
        const nextMsgs = [...latestMessagesRef.current, assistantMsg];
        setMessages(nextMsgs);

        let nextFlow = ingestFlowRef.current;
        if (createdOk) {
          nextFlow = applyIngestTransition(nextFlow, { type: 'confirmed' });
          commitIngestFlow(nextFlow);
        }
        await persistSession(nextMsgs, nextFlow);
        return result.success && !result.skipped;
      } finally {
        setSending(false);
      }
    },
    [workspaceId, sending, persistSession, commitIngestFlow]
  );

  return {
    messages,
    sending,
    send,
    clearMessages,
    restoreMessages,
    prefillRef,
    markChoicesUsed,
    getSessionId,
    ingestFlow,
    applyIngestEvent,
    createPageFromText,
  };
}
